import type { FC } from "react";
import { StarRating } from "@/components/facility/StarRating";

interface ReviewsRowProps {
    score?: number;
    count?: number;
    /** Link target for the "Reviews" text; renders a plain span when empty. */
    url?: string;
    /** BEM block the element classes hang off, e.g. "facility-section"
     *  renders "facility-section__reviews" / "…__reviews-link" / "…__stars". */
    classPrefix: string;
    /** Optional accessible name for the Reviews link (per-facility labelling).
     *  Omit to keep the visible "Reviews" text as the accessible name. */
    ariaLabel?: string;
    /** Centers the row (the nearby-units card lays its info out centered). */
    center?: boolean;
}

/** "Reviews" link + star rating + review count, shown only when both a
 *  numeric score and a positive count are present (the shared reviews gate).
 *  Extracted from NearbyFacilityItem / FacilitySection (WEB-704) so both
 *  render the identical reviews row apart from the class prefix. */
export const ReviewsRow: FC<ReviewsRowProps> = ({
    score,
    count,
    url,
    classPrefix,
    ariaLabel,
    center = false,
}) => {
    if (typeof score !== "number" || typeof count !== "number" || count <= 0) {
        return null;
    }

    return (
        <div
            className={`${classPrefix}__reviews tw:inline-flex tw:items-center${
                center ? " tw:justify-center" : ""
            } tw:gap-1 tw:text-sm`}
        >
            {url ? (
                <a
                    href={url}
                    className={`${classPrefix}__reviews-link tw:font-medium tw:underline`}
                    aria-label={ariaLabel}
                >
                    Reviews
                </a>
            ) : (
                // No link target: plain text — drop the underline so it
                // doesn't fake a link affordance.
                <span className={`${classPrefix}__reviews-link tw:font-medium`}>
                    Reviews
                </span>
            )}
            <StarRating score={score} classPrefix={classPrefix} />
            <span className={`${classPrefix}__reviews-count tw:text-gray-600`}>
                ({count})
            </span>
        </div>
    );
};
